import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { Creative, DisplayCreative, TChannel } from '@feathr/blackbox';

import type { ICreativesFilters } from './ProjectCreativesPage.types';
import {
  getCombinedClasses,
  getSelectedChannel,
  hasActiveFilters,
} from './ProjectCreativesPage.utils';

interface IUseSelectedChannelParams {
  filters: ICreativesFilters;
  setFilters: React.Dispatch<React.SetStateAction<ICreativesFilters>>;
}

interface IUseSelectedChannelReturn {
  /** The currently selected channel derived from the class filter. */
  selectedChannel: TChannel | undefined;
  /** Handler for when dimensions are selected. */
  handleDimensionsChange: (spec: string) => void;
  /** Handler for when dimensions are cleared. */
  handleDimensionsClear: () => void;
}

/**
 * Hook that manages channel selection state and its effects on dimension filtering.
 *
 * Automatically clears the spec filter when the channel changes (since dimensions
 * are channel-specific).
 *
 * @param params - The filters state and setter
 * @returns The selected channel and dimension change handlers
 *
 * @example
 * const { selectedChannel, handleDimensionsChange, handleDimensionsClear } = useSelectedChannel({
 *   filters,
 *   setFilters,
 * });
 */
export function useSelectedChannel({
  filters,
  setFilters,
}: IUseSelectedChannelParams): IUseSelectedChannelReturn {
  const selectedChannel = useMemo(() => getSelectedChannel(filters.channel), [filters.channel]);

  const prevChannelRef = useRef(selectedChannel);
  useEffect(() => {
    if (prevChannelRef.current !== selectedChannel && filters.spec) {
      setFilters((prev) => ({ ...prev, spec: undefined }));
    }
    prevChannelRef.current = selectedChannel;
  }, [selectedChannel, filters.spec, setFilters]);

  const handleDimensionsChange = useCallback(
    (spec: string) => {
      setFilters((prev) => ({ ...prev, spec }));
    },
    [setFilters],
  );

  const handleDimensionsClear = useCallback(() => {
    setFilters((prev) => ({ ...prev, spec: undefined }));
  }, [setFilters]);

  return {
    selectedChannel,
    handleDimensionsChange,
    handleDimensionsClear,
  };
}

interface IUseProjectCreativesPageParams {
  eventId: string;
}

interface IUseProjectCreativesPageReturn {
  /** The current filter state. */
  filters: ICreativesFilters;
  /** Updates the filter state. */
  setFilters: React.Dispatch<React.SetStateAction<ICreativesFilters>>;
  /** The creative currently being edited, or null. */
  editingCreative: DisplayCreative | null;
  /** Opens the edit modal for a creative. */
  handleEditCreative: (creative: Creative) => void;
  /** Closes the edit modal. */
  handleCloseEditModal: () => void;
  /** The filters to pass to the API/Table, with combined classes. */
  tableFilters: Record<string, unknown>;
  /** Whether any user-applied filters are active. */
  hasActiveFilters: boolean;
  /** The currently selected channel derived from filters. */
  selectedChannel: TChannel | undefined;
  /** Handler for when dimensions are selected. */
  handleDimensionsChange: (spec: string) => void;
  /** Handler for when dimensions are cleared. */
  handleDimensionsClear: () => void;
}

/**
 * Main hook for the ProjectCreativesPage that manages all state and derived values.
 *
 * Encapsulates:
 * - Filter state management
 * - Edit modal state
 * - Combined class calculation for API filters
 * - Channel-specific dimension handling
 *
 * @param params - The event ID from route params
 * @returns All state and handlers needed by the page component
 *
 * @example
 * const {
 *   filters,
 *   setFilters,
 *   tableFilters,
 *   hasActiveFilters,
 *   ...
 * } = useProjectCreativesPage({ eventId });
 */
export function useProjectCreativesPage({
  eventId,
}: IUseProjectCreativesPageParams): IUseProjectCreativesPageReturn {
  const [filters, setFilters] = useState<ICreativesFilters>({});
  const [editingCreative, setEditingCreative] = useState<DisplayCreative | null>(null);

  const handleEditCreative = useCallback((creative: Creative): void => {
    setEditingCreative(creative as DisplayCreative);
  }, []);

  const handleCloseEditModal = useCallback((): void => {
    setEditingCreative(null);
  }, []);

  const { classes: combinedClasses, isImpossible: isImpossibleFilter } = useMemo(
    () => getCombinedClasses(filters),
    [filters],
  );

  const tableFilters = useMemo(
    () => ({
      name__icontains: filters.name__icontains,
      spec: filters.spec,
      _cls__in: combinedClasses,
      event: eventId,
      ...(isImpossibleFilter ? { id: 'impossible' } : {}),
    }),
    [filters.name__icontains, filters.spec, combinedClasses, eventId, isImpossibleFilter],
  );

  const activeFilters = useMemo(() => hasActiveFilters(filters), [filters]);

  const { selectedChannel, handleDimensionsChange, handleDimensionsClear } = useSelectedChannel({
    filters,
    setFilters,
  });

  return {
    filters,
    setFilters,
    editingCreative,
    handleEditCreative,
    handleCloseEditModal,
    tableFilters,
    hasActiveFilters: activeFilters,
    selectedChannel,
    handleDimensionsChange,
    handleDimensionsClear,
  };
}
